import ProgressBar from '@/components/buy-stack/ProgressBar'
import Question from '@/components/quiz/Question'
import type { QuizQuestion } from '@/components/quiz/quiz-question'
import { useHeight } from '@/hooks/use-height'
import backArrowIcon from '@/icons/back-arrow-icon-2.svg'
import nextArrowIcon from '@/icons/next-arrow-icon.svg'
import { getLocalizedStrings } from '@/localization/localization-store'
import withRootRoute from '@/localization/with-root-route'
import { logInfo } from '@/utilities/log'
import { preloadImage } from '@/utilities/preload-image'
import type { ChangeEventHandler } from 'preact/compat'
import { useEffect, useRef, useState } from 'preact/hooks'
import AnimateHeight from 'react-animate-height'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import QuizComplete from './QuizComplete'
import QuizLoading from './QuizLoading'

export type QuizProps = {
  skipLinkUrl?: string
  skipLinkLabel?: string
  questions: QuizQuestion[]
  loadingMessages?: string[]
  completePage?: JSX.Element
}

type QuizStatus = 'inProgress' | 'loading' | 'complete'

export default function Quiz({
  skipLinkUrl = '/shopnow',
  skipLinkLabel,
  questions,
  loadingMessages,
  completePage = <QuizComplete />
}: QuizProps) {
  const [step, setStep] = useState(0)
  const [status, setStatus] = useState<QuizStatus>('inProgress')
  const [quizData, setQuizData] = useState<Map<string, string>>(new Map())
  const { storeRootRoute } = getLocalizedStrings()

  useEffect(() => {
    for (const question of questions) {
      for (const option of question.options) {
        if (option.image) {
          preloadImage(option.image.src)
        }
      }
    }
  }, [questions])

  const handleNextStep = () => {
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100)

    if (step < questions.length - 1) {
      setStep(prev => prev + 1)
      return
    }

    setStatus('loading')
  }

  const handlePreviousStep = () => {
    if (step === 0) return

    setStep(prev => prev - 1)
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100)
  }

  const handleLoadingComplete = () => {
    setStatus('complete')
  }

  const handleQuestionChange: ChangeEventHandler<HTMLInputElement> = e => {
    const answer = e.currentTarget.value
    const questionName = e.currentTarget.name
    const newQuizData = new Map(quizData)
    newQuizData.set(questionName, answer)
    setQuizData(newQuizData)
  }

  const containerRef = useRef<HTMLDivElement>(null)
  const height = useHeight(containerRef)

  const question = questions[step]
  const answer = quizData.get(question.name)
  const enableNextButton = !!answer || question.options.length === 0

  useEffect(() => {
    switch (status) {
      case 'inProgress':
        logInfo('QuizQuestionViewed', {
          step: step + 1,
          questionName: question.name,
          question,
          answer
        })
        break
      case 'loading':
        logInfo('QuizLoading')
        break
      case 'complete':
        logInfo('QuizCompleted', {
          quizData: Object.fromEntries(quizData)
        })
        break
    }
  }, [step, question, status, answer, quizData])

  if (status === 'loading') {
    return (
      <div className='flex h-[60dvh] items-center justify-center'>
        <QuizLoading messages={loadingMessages} onLoadingComplete={handleLoadingComplete} />
      </div>
    )
  }

  if (status === 'complete') {
    return completePage
  }

  return (
    <div className='flex flex-col items-center gap-y-4 md:gap-y-6'>
      <div className='mb-32 flex flex-col items-center gap-y-4 px-5vw pb-3 md:m-0 md:gap-y-6'>
        <ProgressBar
          currentStep={step + 1}
          stepsCount={questions.length + 1}
          lastStepMarker={<span className='text-2xl'>🎉</span>}
        />
        <AnimateHeight height={height} duration={200} easing={'ease-in-out'}>
          <div ref={containerRef}>
            <TransitionGroup>
              <CSSTransition key={question} timeout={500} classNames='fade'>
                <Question question={question} value={answer} onChange={handleQuestionChange} />
              </CSSTransition>
            </TransitionGroup>
          </div>
        </AnimateHeight>
      </div>
      <div className='fixed bottom-0 mx-auto flex w-full flex-col gap-y-2 bg-cloud-white p-3 md:static md:w-max md:bg-transparent'>
        <div
          className={`grid w-full max-w-128 ${step === 0 ? 'grid-cols-1' : 'grid-cols-[max-content,minmax(0,1fr)]'} gap-x-2`}>
          {step > 0 && (
            <button
              type='button'
              className={
                'btn btn-neutral btn-square btn-outline flex h-full max-h-[3.125rem] items-center justify-center px-4 py-0'
              }
              onClick={handlePreviousStep}>
              <img
                alt='Back button icon'
                className='h-4 w-auto'
                loading='lazy'
                src={backArrowIcon}
              />
            </button>
          )}
          <button
            type='button'
            className={`btn btn-square w-full md:w-auto lg:px-20`}
            disabled={!enableNextButton}
            onClick={enableNextButton ? handleNextStep : undefined}>
            {step === 0 ? 'CONTINUE' : 'NEXT'}
            <span className='ml-2 inline-block h-4 w-6'>
              <img
                src={nextArrowIcon}
                alt='Next Arrow'
                height={16}
                width={16}
                className={enableNextButton ? '' : 'opacity-0'}
              />
            </span>
          </button>
        </div>
        <span>
          👉{' '}
          <a
            className='text-xs uppercase tracking-normal  sm:text-sm'
            href={withRootRoute(skipLinkUrl, storeRootRoute)}
            onClick={() => logInfo('QuizSkipLinkClicked')}>
            {skipLinkLabel}
          </a>
        </span>
      </div>
    </div>
  )
}
